/**
 * Rhythmz — Scene: Crystal Colossus
 * ──────────────────────────────────
 * A towering entity made of interlocking faceted crystals. Real lighting from
 * SceneLighting. Each crystal is a separate InstancedMesh or Mesh with its own
 * vertex displacement via TSL positionNode.
 *
 * Audio reactivity:
 *   Bass → crystal size pulse, shoulder orbit speed
 *   Beat → head flash, color shift
 *   Treble → small facet vibration
 */

import * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, float, sin, fract, dot, floor, abs,
  add, sub, mul, mix, pow, normalize,
  positionLocal, normalLocal, positionWorld, cameraPosition, instanceIndex,
} from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface CrystalUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

/* ── 3D noise for crystal surface displacement ── */
const hash3 = Fn(([p]: [N]) => fract(sin(dot(p, vec3(127.1, 311.7, 74.7))).mul(float(43758.5453))));

const vnoise3 = Fn(([p]: [N]) => {
  const i = floor(p);
  const f: N = fract(p);
  const ux = mul(f.x, f.x, sub(float(3.0), mul(float(2.0), f.x)));
  const uy = mul(f.y, f.y, sub(float(3.0), mul(float(2.0), f.y)));
  const uz = mul(f.z, f.z, sub(float(3.0), mul(float(2.0), f.z)));
  const n000 = hash3(i), n100 = hash3(add(i, vec3(1,0,0)));
  const n010 = hash3(add(i, vec3(0,1,0))), n110 = hash3(add(i, vec3(1,1,0)));
  const n001 = hash3(add(i, vec3(0,0,1))), n101 = hash3(add(i, vec3(1,0,1)));
  const n011 = hash3(add(i, vec3(0,1,1))), n111 = hash3(add(i, vec3(1,1,1)));
  const nx00=mix(n000,n100,ux), nx10=mix(n010,n110,ux), nx01=mix(n001,n101,ux), nx11=mix(n011,n111,ux);
  return mix(mix(nx00,nx10,uy), mix(nx01,nx11,uy), uz);
});

const fbm3 = Fn(([p]: [N]) => {
  let v: N = float(0.0);
  let a: N = float(0.5);
  let freq: N = float(1.0);
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq))));
  return v;
});

const instanceSeed = Fn(() => {
  const idx = instanceIndex.toFloat();
  return fract(sin(add(mul(idx, float(0.159)), mul(idx, idx, float(0.00731)))).mul(float(43758.5453)));
});

export function createCrystalColossus(u: CrystalUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(0.5, 0.7, 1.0),    // cool blue-white
    keyIntensity: 10,
    keyPosition: new THREE.Vector3(6, 8, 4),
    fillColor: new THREE.Color(0.2, 0.3, 0.6),   // dim blue
    fillIntensity: 3,
    fillPosition: new THREE.Vector3(-4, 0, 6),
    rimColor: new THREE.Color(1.0, 0.4, 0.2),    // warm orange rim
    rimIntensity: 7,
    rimPosition: new THREE.Vector3(-3, -2, -6),
    camLightIntensity: 3,
  });
  group.add(lighting.group);

  // ── Helper: create a crystal mesh with TSL displacement + material ──
  function makeCrystal(geo: THREE.BufferGeometry, scale: number, pos: THREE.Vector3, isHead = false): THREE.Mesh {
    const mat = new THREE.MeshStandardNodeMaterial({
      metalness: 0.3,
      roughness: 0.15,
    });

    const noiseAt = (p: N, seed: N) => fbm3(add(mul(p, float(2.0)), add(mul(u.uTime, float(0.3)), mul(seed, float(3.0)))));

    // Vertex displacement: faceted noise + audio pulse
    mat.positionNode = Fn(() => {
      const posL = positionLocal;
      const norm = normalLocal;
      const seed = isHead ? float(0.0) : instanceSeed();
      const n = noiseAt(posL, seed);
      const disp = mul(add(sub(n, float(0.5)), mul(u.uBass, float(0.6)), mul(u.uBeat, float(0.3))), float(0.3));
      const spike = pow(abs(disp), float(0.8));
      return add(posL, mul(norm, spike));
    })();

    // Normal recompute
    mat.normalNode = Fn(() => {
      const posL = positionLocal;
      const seed = isHead ? float(0.0) : instanceSeed();
      const n = noiseAt(posL, seed);
      const eps = float(0.04);
      const nX = noiseAt(add(posL, vec3(eps, 0, 0)), seed);
      const nZ = noiseAt(add(posL, vec3(0, 0, eps)), seed);
      return normalize(vec3(sub(nX, n), float(0.5), sub(nZ, n)));
    })();

    // Emissive: internal glow with fresnel rim
    mat.emissiveNode = Fn(() => {
      const seed = isHead ? float(0.0) : instanceSeed();
      const n = noiseAt(positionLocal, seed);
      const viewDir = normalize(cameraPosition.sub(positionWorld));
      const fres = sub(float(1.0), abs(dot(normalLocal, viewDir)));
      const rim = pow(fres, float(2.5));
      // Palette shift per crystal
      const colShift = isHead ? float(0.0) : mul(seed, float(0.2));
      const baseCol: N = mix(u.uCol0, u.uCol1, fract(add(u.uTime, mul(n, float(0.15)), colShift)));
      const rimBright: N = add(mul(baseCol, float(0.3)), mul(baseCol, mul(rim, float(0.9))));
      return vec4(rimBright.x, rimBright.y, rimBright.z, float(1.0));
    })();

    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    mesh.scale.setScalar(scale);
    return mesh;
  }

  // ── Body: vertical stack of dodecahedra ──
  const bodyParts: THREE.Mesh[] = [];
  const bodyCount = 5;
  const bodyGeo = new THREE.DodecahedronGeometry(0.5, 0);
  for (let i = 0; i < bodyCount; i++) {
    const yPos = -1.2 + i * 0.55;
    const s = 0.6 + (1 - i / bodyCount) * 0.4;
    const mesh = makeCrystal(bodyGeo, s, new THREE.Vector3(0, yPos, 0));
    group.add(mesh);
    bodyParts.push(mesh);
  }

  // ── Shoulder crystals (instanced, orbiting) ──
  const shoulderCount = 8;
  const shoulderGeo = new THREE.IcosahedronGeometry(0.3, 1);
  const shoulderMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.3, roughness: 0.15 });

  const noiseAtS = (p: N, seed: N) => fbm3(add(mul(p, float(2.0)), add(mul(u.uTime, float(0.3)), mul(seed, float(3.0)))));

  shoulderMat.positionNode = Fn(() => {
    const posL = positionLocal;
    const norm = normalLocal;
    const seed = instanceSeed();
    const n = noiseAtS(posL, seed);
    const disp = mul(add(sub(n, float(0.5)), mul(u.uBass, float(0.5))), float(0.3));
    return add(posL, mul(norm, pow(abs(disp), float(0.8))));
  })();

  shoulderMat.normalNode = Fn(() => {
    const posL = positionLocal;
    const seed = instanceSeed();
    const n = noiseAtS(posL, seed);
    const eps = float(0.04);
    const nX = noiseAtS(add(posL, vec3(eps, 0, 0)), seed);
    const nZ = noiseAtS(add(posL, vec3(0, 0, eps)), seed);
    return normalize(vec3(sub(nX, n), float(0.5), sub(nZ, n)));
  })();

  shoulderMat.emissiveNode = Fn(() => {
    const seed = instanceSeed();
    const n = noiseAtS(positionLocal, seed);
    const viewDir = normalize(cameraPosition.sub(positionWorld));
    const fres = sub(float(1.0), abs(dot(normalLocal, viewDir)));
    const rim = pow(fres, float(2.5));
    const colShift = mul(seed, float(0.25));
    const baseCol: N = mix(u.uCol0, u.uCol1, fract(add(u.uTime, mul(n, float(0.15)), colShift)));
    const rimBright: N = add(mul(baseCol, float(0.2)), mul(baseCol, mul(rim, float(0.8))));
    return vec4(rimBright.x, rimBright.y, rimBright.z, float(1.0));
  })();

  const shoulders = new THREE.InstancedMesh(shoulderGeo, shoulderMat, shoulderCount);
  shoulders.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(shoulders);

  // ── Head crystal ──
  const headGeo = new THREE.OctahedronGeometry(0.5, 1);
  const head = makeCrystal(headGeo, 0.7, new THREE.Vector3(0, 1.3, 0), true);
  group.add(head);

  // ── CPU-side orbit state for shoulders ──
  const orbitData = Array.from({ length: shoulderCount }, () => ({
    radius: 1.0 + Math.random() * 0.8,
    speed: 0.3 + Math.random() * 0.5,
    offset: Math.random() * 6.28,
    yOff: -0.5 + Math.random() * 1.0,
  }));

  let t = 0;

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = {
      bass: u.uBass.value ?? 0,
      beat: u.uBeat.value ?? 0,
      mid: u.uMid.value ?? 0,
      level: (u.uBass.value! + u.uMid.value!) * 0.5,
    };
    lighting.update(audio);

    // Body rotation — slow, majestic
    group.rotation.y = t * 0.12;
    group.rotation.x = Math.sin(t * 0.07) * 0.1;

    // Head independent rotation
    head.rotation.x = Math.sin(t * 0.3) * 0.2;
    head.rotation.y = t * 0.5;
    // Head flash on beat
    const beatFlash = 1 + (u.uBeat.value ?? 0) * 0.3;
    head.scale.setScalar(0.7 * beatFlash);

    // Body crystal pulse
    bodyParts.forEach((part, i) => {
      const pulse = 1 + (u.uBass.value ?? 0) * 0.08 * (1 - i / bodyCount);
      const s = (0.6 + (1 - i / bodyCount) * 0.4) * pulse;
      part.scale.setScalar(s);
    });

    // Update shoulder orbits
    const pivot = new THREE.Object3D();
    for (let i = 0; i < shoulderCount; i++) {
      const d = orbitData[i];
      const a = d.offset + t * d.speed;
      const r = d.radius + 0.15 * Math.sin(t * 0.3 + d.offset);
      const bassBoost = 1 + (u.uBass.value ?? 0) * 0.12;
      pivot.position.set(Math.cos(a) * r * bassBoost, d.yOff + Math.sin(t * 0.5 + d.offset) * 0.2, Math.sin(a) * r * bassBoost);
      pivot.rotation.set(t * 0.2 * d.speed, t * 0.3 * d.speed, 0);
      pivot.scale.setScalar(0.7 + 0.1 * Math.sin(t * d.speed + d.offset));
      pivot.updateMatrix();
      shoulders.setMatrixAt(i, pivot.matrix);
    }
    shoulders.instanceMatrix.needsUpdate = true;
  };

  const dispose = (): void => {
    lighting.dispose();
    bodyParts.forEach(p => { p.geometry.dispose(); (p.material as THREE.Material).dispose(); });
    head.geometry.dispose(); (head.material as THREE.Material).dispose();
    shoulderGeo.dispose(); shoulderMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Crystal Colossus' };
}